import React, {useEffect, useRef} from 'react'
import ScrollBar from 'react-perfect-scrollbar'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {DataObjectId, GTDStage} from 'yougile-model'
import {UserSettings} from '@client/user-settings'
import {DivWithHint} from '@components/common/hint/hint'
import {SVGPreload} from '@components/common/svg-preload/svg-preload'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {TasksLists} from '@components/my-tasks/tasks-list'
import {keyboardHandler, useInput, useLocalStorage} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {Task} from '@extensions/task'
import {
  getDefaultSortFromPage,
  SortKey,
  sortTypes,
  TaskPage
} from './my-tasks-conf'
import {MyTasksList} from './my-tasks-list'
import {getTasks} from './my-tasks-store'
import {processUnseen} from './process-unseen'
import {TaskItem} from './task-item'

const TwBase = tw.div`
  rounded-b-4
  bg-main-old
  h-full
`

interface Props {
  isOthers: boolean
  tasks: TaskItem[]
  toggleCompleted: (task: TaskItem) => void
  setStage: (task: TaskItem, stage: GTDStage, reason: string) => void
  setOrder(key: string, order: DataObjectId[]): void
  draggedId: DataObjectId | undefined
  setDrag: (e: React.DragEvent, taskId: DataObjectId) => void
  itemClick: (task: TaskItem) => () => void
  page: TaskPage
  setPage: (page: TaskPage) => void
}

const AddTask = ({page}: Pick<Props, 'page'>) => {
  const bem = Util.bemFrom('my-tasks')
  const [addingPrivate, setAddingPrivate] = React.useState(false)
  const [text, onChange, setText] = useInput('')
  const startAddPrivate = () => setAddingPrivate(true)
  const cancelAddTask = () => {
    setText('')
    setAddingPrivate(false)
    return true
  }
  const submit = () => {
    if (text.trim() && globals.currentUserId) {
      setText('')
      if (page === 'my') {
        Task.createTask(text.trim(), {assigned: [globals.currentUserId]})
      } else if (page === 'by-me') {
        Task.createTask(text.trim(), {assignedBy: globals.currentUserId})
      } else if (page === 'starred') {
        const task = Task.createTask(text.trim(), {})
        if (task) {
          UserSettings.setStarred(task, true, 'my-tasks-favourite')
        }
      } else {
        Task.createTask(text.trim(), {
          assigned: [globals.currentUserId],
          isPrivate: true
        })
      }
    } else {
      cancelAddTask()
    }
  }
  const blur = () => {
    submit()
    cancelAddTask()
  }
  const keyDown = keyboardHandler({enter: submit, escape: cancelAddTask})
  return (
    <div className={bem('add-private', {adding: addingPrivate})}>
      {addingPrivate ? (
        <input
          autoFocus={true}
          placeholder={Lang.msg.taskPlaceholder}
          className={bem('add-private-input')}
          value={text}
          onChange={onChange}
          onKeyDown={keyDown}
          onBlur={blur}
        />
      ) : (
        <div className={bem('add-private-btn')} onClick={startAddPrivate}>
          {Lang.msg.addTask}
        </div>
      )}
    </div>
  )
}

// eslint-disable-next-line max-lines-per-function
export const MyTasksBase = (props: Props) => {
  const {
    tasks,
    toggleCompleted,
    setStage,
    setOrder,
    draggedId,
    setDrag,
    itemClick,
    page,
    setPage,
    isOthers
  } = props
  const scrollContainer = useRef<ScrollBar>(null)

  const bem = Util.bemFrom('my-tasks')
  const [sort, setSort] = useLocalStorage<SortKey>(
    `my-tasks-sort-${page}`,
    getDefaultSortFromPage(page)
  )
  const sortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    setSort(e.target.value as SortKey)
  }
  const commonProps = {
    toggleCompleted,
    setStage,
    setOrder,
    draggedId,
    setDrag,
    itemClick
  }
  const clickPage = (p: TaskPage) => () => {
    setPage(p)
  }

  const nav = (p: TaskPage, title: string, hint: string) => (
    <DivWithHint
      content={hint}
      className={bem('nav-item', {current: page === p, ['page-' + p]: true})}
      onClick={clickPage(p)}
    >
      {title}
    </DivWithHint>
  )

  const sortType = sortTypes.get(sort)
  const order = UserSettings.getOrder(`${page}-tasks`)
  const currentTasks = getTasks(tasks, 'current', order)
  const doneTasks = getTasks(tasks, 'done').sort(
    (a, b) => b.completedAt - a.completedAt
  )
  if (sortType?.getComparator) {
    const comparator = sortType.getComparator(page)
    currentTasks.sort(comparator)
  }

  const groupped = sortType?.getGrouping
    ? sortType.getGrouping(page, currentTasks)
    : undefined

  useEffect(() => {
    if (scrollContainer.current) {
      scrollContainer.current.updateScroll()
    }
  }, [tasks?.length])

  const unseen = tasks.filter(t => !t.stage)
  if (unseen.length > 0 && !globals.silentMode) {
    setTimeout(async () => {
      const toProcess = unseen.splice(0, 200)
      void processUnseen(toProcess)
    }, 500)
  }

  return (
    <div className="flex flex-col h-full pb-8">
      {isOthers ? (
        <div className={bem('nav')}>
          {nav(
            'by-people',
            Lang.msg.taskPage.byPeopleTab,
            Lang.msg.taskPage.byPeopleHint
          )}
          {nav(
            'not-assigned',
            Lang.msg.taskPage.notAssignedTab,
            Lang.msg.taskPage.notAssignedHint
          )}
          {nav(
            'expired',
            Lang.msg.taskPage.expiredTab,
            Lang.msg.taskPage.expiredHint
          )}
        </div>
      ) : (
        <div className={bem('nav')}>
          {nav('my', Lang.msg.taskPage.myTasks, Lang.msg.taskPage.myTasksHint)}
          {nav('by-me', Lang.msg.taskPage.byMe, Lang.msg.taskPage.byMeHint)}
          {nav(
            'private',
            Lang.msg.taskPage.privateList,
            Lang.msg.taskPage.privateListHint
          )}
          {nav(
            'starred',
            Lang.msg.taskPage.starredTab,
            Lang.msg.taskPage.starredHint
          )}
        </div>
      )}

      {page === 'my' && <TasksLists />}

      {page !== 'my' && (
        <TwBase>
          <WithPerfectScrollbar scrollDirection="xy" ref={scrollContainer}>
            <div className={`${bem()} min-w-720`}>
              <SVGPreload
                img={[
                  'gtd/done',
                  'gtd/undone',
                  'gtd/drag',
                  'gtd/taskinfo',
                  'gtd/gototask'
                ]}
              />

              <div className={bem('panel')}>
                <div className={bem('options', {noAdd: isOthers})}>
                  <div className={bem('sort')}>
                    <div className={bem('sort-title')}>
                      {Lang.msg.taskPage.sortBy}:
                    </div>
                    <select
                      className={bem('sort-select')}
                      value={sort}
                      onChange={sortChange}
                    >
                      {[...sortTypes.values()].map(t => (
                        <option value={t.key} key={t.key}>
                          {t.getName()}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {!isOthers && <AddTask page={page} />}

                {groupped ? (
                  <div className={bem('grouping')}>
                    {groupped.map(g => (
                      <MyTasksList
                        key={g.id}
                        tasks={g.items}
                        title={g.title}
                        stage={'current'}
                        hideTitle={false}
                        page={page}
                        sort={sort}
                        initialExpanded={groupped.length < 2}
                        {...commonProps}
                      />
                    ))}
                  </div>
                ) : (
                  <>
                    <MyTasksList
                      tasks={currentTasks}
                      title={Lang.msg.myTasksCurrent}
                      stage={'current'}
                      hideTitle={true}
                      page={page}
                      sort={sort}
                      initialExpanded={true}
                      {...commonProps}
                    />
                    {page !== 'expired' && (
                      <div className={bem('grouping')}>
                        <MyTasksList
                          tasks={doneTasks}
                          title={Lang.msg.myTasksDone}
                          stage={'done'}
                          page={page}
                          sort={sort}
                          initialExpanded={false}
                          {...commonProps}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </WithPerfectScrollbar>
        </TwBase>
      )}
    </div>
  )
}
